import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getConfig } from '../lib/config';
import * as publicApi from '../api/public';
import { ErrorAlert, Field, Spinner } from '../components/ui';
import { safeRedirectPath } from '../lib/safePath';
import { ui, cn } from '../lib/ui';

export default function OrgLogin() {
  const { orgSlug } = useParams();
  const { login, completeMfaLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { productName } = getConfig();

  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState('');

  const runIdRef = useRef(0);

  const fetchOrg = useCallback(async () => {
    const id = ++runIdRef.current;
    try {
      const data = await publicApi.getOrgProfile(orgSlug);
      if (id !== runIdRef.current) return;
      setOrg(data);
    } catch {
      if (id !== runIdRef.current) return;
    } finally {
      if (id === runIdRef.current) setOrgLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  // Back to where the sign-in link came from (the donate page sends its own
  // path), checked by safeRedirectPath so only an in-app path is followed.
  const destination = safeRedirectPath(location.state && location.state.from);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const credentials = identifier.includes('@')
        ? { email: identifier, password }
        : { username: identifier, password };
      const result = await login(credentials);
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
      } else {
        navigate(destination, { replace: true });
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitMfa(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await completeMfaLogin(mfaToken, { code });
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (orgLoading) {
    return (
      <div className={ui.authScreen}>
        <Spinner />
      </div>
    );
  }

  const brandName = org?.name || productName;

  if (mfaToken) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <div className={ui.authBrand}>
            {org?.logoUrl && (
              <img src={org.logoUrl} alt={`${brandName} logo`} className={ui.publicOrgLogo} />
            )}
            <h1 className={ui.h1}>{brandName}</h1>
            <p className={cn(ui.muted, 'text-body-s')}>Two-factor authentication</p>
          </div>
          {error && <ErrorAlert message={error.message || String(error)} />}
          <form onSubmit={onSubmitMfa}>
            <Field
              label="Authentication code"
              value={code}
              onChange={e => setCode(e.target.value)}
              autoFocus
            />
            <button className={cn(ui.btnPrimary, 'w-full')} type="submit" disabled={busy}>
              {busy ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <div className={ui.authBrand}>
          {org?.logoUrl && (
            <img src={org.logoUrl} alt={`${brandName} logo`} className={ui.publicOrgLogo} />
          )}
          <h1 className={ui.h1}>{brandName}</h1>
        </div>

        {error && <ErrorAlert message={error.message || String(error)} />}

        <form onSubmit={onSubmit}>
          <Field
            label="Email or username"
            value={identifier}
            onChange={e => setIdentifier(e.target.value)}
            autoFocus
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button
            className={cn(ui.btnPrimary, 'w-full')}
            type="submit"
            disabled={busy || !identifier || !password}
          >
            {busy ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="text-body-s mt-4 text-center">
          Don&apos;t have an account?{' '}
          <Link to={`/o/${orgSlug}/register`}>Join {brandName}</Link>
        </p>
      </div>
    </div>
  );
}
